import React from 'react'
import { Table } from 'antd'
import { useTranslation } from 'react-i18next'
import BupSpinner from '@BUP/components/Loading/Spinner'
import AlertComponent from '@BUP/components/Generics/Alert'
import { useGradeName } from '@BUP/hooks/CourseAssignment/useGradeName'

/**
 * At-a-glance progress of every open course-assignment period, one row per grade plus a total:
 * how many students the grade has, how many already answered, and the percentage. The dashboard
 * below it only shows one grade at a time, so without this a counselor running several grades at
 * once had to switch the grade selector back and forth to know how far along each one was.
 *
 * Rows follow `grades` (the same order as the dashboard's grade selector); a grade missing from it
 * goes last, by period id.
 */
const OpenPeriodsSummary = ({ isLoading, error, periods, grades = [] }) => {
  const [t, i18n] = useTranslation('translation', { keyPrefix: 'pages.data.courseAssignmentPeriodDashboard' })
  const gradeName = useGradeName()

  if (isLoading) return <BupSpinner size='small' />

  if (error) {
    return (
      <AlertComponent
        type='error'
        messageKey='common.resources.course-assignment.actions.read.failure'
        error={error}
      />
    )
  }

  const openPeriods = periods.filter(period => period.status === 'open')
  if (!openPeriods.length) {
    return <p>{t('openPeriodsEmpty')}</p>
  }

  const gradeOrder = (grade) => {
    const index = grades.indexOf(grade)
    return index === -1 ? Number.MAX_SAFE_INTEGER : index
  }
  const rows = [...openPeriods].sort((a, b) =>
    gradeOrder(a.grade) - gradeOrder(b.grade) || Number(a.id) - Number(b.id)
  )

  const numberFormat = new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  const formatProgress = (completed, eligible) =>
    eligible ? `${numberFormat.format((completed / eligible) * 100)}%` : '-'

  const totalEligible = rows.reduce((sum, period) => sum + (period.eligibleCount ?? 0), 0)
  const totalCompleted = rows.reduce((sum, period) => sum + (period.completedCount ?? 0), 0)

  const columns = [
    { title: t('openPeriodsColumnPeriod'), dataIndex: 'id', key: 'id' },
    { title: t('openPeriodsColumnGrade'), dataIndex: 'grade', key: 'grade', render: grade => gradeName(grade) },
    { title: t('openPeriodsColumnStudents'), dataIndex: 'eligibleCount', key: 'eligibleCount', align: 'right' },
    { title: t('openPeriodsColumnCompleted'), dataIndex: 'completedCount', key: 'completedCount', align: 'right' },
    {
      title: t('openPeriodsColumnProgress'),
      key: 'progress',
      align: 'right',
      render: (_, period) => formatProgress(period.completedCount ?? 0, period.eligibleCount ?? 0)
    }
  ]

  return (
    <Table
      columns={columns}
      dataSource={rows}
      rowKey='id'
      pagination={false}
      size='small'
      scroll={{ x: true }}
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}><strong>{t('openPeriodsTotal')}</strong></Table.Summary.Cell>
          <Table.Summary.Cell index={1} />
          <Table.Summary.Cell index={2} align='right'><strong>{totalEligible}</strong></Table.Summary.Cell>
          <Table.Summary.Cell index={3} align='right'><strong>{totalCompleted}</strong></Table.Summary.Cell>
          <Table.Summary.Cell index={4} align='right'>
            <strong>{formatProgress(totalCompleted, totalEligible)}</strong>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  )
}

export default OpenPeriodsSummary
